import { CacheService, CustomerService, IApplicationFilter, getChartReadyPromise, property, widget } from '@dynamik/common';
import { color, create, percent, unuseTheme, useTheme } from '@amcharts/amcharts4/core';
import { PieChart, PieSeries } from '@amcharts/amcharts4/charts';
import { PorfolioReportViewModel, PortfolioClient } from '@dynamik/common/clients/reporting-clients';
import { BindingEngine } from 'aurelia-binding';
import { SliceGrouper } from '@amcharts/amcharts4/plugins/sliceGrouper';
import { ChartWidgetBase } from '@dynamik/common';
import { customElement } from 'aurelia-templating';

@customElement('portfolio-by-type-widget')
@widget('Portfolio by type', true, true, 'Description')
export class PortfolioByTypeWidget extends ChartWidgetBase {
  constructor(element: Element, customerSettings: CustomerService, private portfolioClient: PortfolioClient, bindingEngine: BindingEngine, private cacheService: CacheService) {
    super(element, bindingEngine, customerSettings);
  }

  chartObject: PieChart;
  grouper: SliceGrouper;

  @property('Style')
  radius?: number = 60;

  @property('Style')
  otherThreshold: number = 5;

  @property('Style')
  chartTheme: string;

  @property('Style')
  labelColour?: string;

  override async loadData(filter: IApplicationFilter) {
    return this.cacheService.getOrSet(`portfolio-by-type-${filter.customer.companyId}-${filter.dates.toStr}`,
      () => this.portfolioClient.byType(filter.customer.companyId, filter.dates.toStr),
      60000, false);
  }

  override async setData(data: PorfolioReportViewModel[]) {
    this.chartObject?.dispose();
    const theme = this.customerService.getChartTheme(this.chartTheme);
    useTheme(theme);
    this.chartObject = create(this.chart, PieChart);
    const readyPromise = getChartReadyPromise(this.chartObject);
    this.chartObject.radius = percent(this.radius ?? 60);
    this.chartObject.innerRadius = percent(0);
    this.chartObject.hiddenState.properties.opacity = 0;
    const pieSeries = this.chartObject.series.push(new PieSeries());
    pieSeries.slices.template.cornerRadius = 6;
    pieSeries.dataFields.value = 'estimatedValue';
    pieSeries.dataFields.category = 'categoryName';
    if (this.otherThreshold !== undefined) {
      this.grouper = pieSeries.plugins.push(new SliceGrouper());
      this.grouper.target = pieSeries;
      this.grouper.threshold = this.otherThreshold;
    }

    pieSeries.slices.template.stroke = color('#343a40');
    pieSeries.slices.template.cornerRadius = 8;
    pieSeries.slices.template.strokeWidth = 2;
    pieSeries.slices.template.strokeOpacity = 1;
    pieSeries.numberFormatter.numberFormat = '#,###.';
    pieSeries.alignLabels = false;
    pieSeries.ticks.template.stroke = pieSeries.labels.template.fill = color(this.getChartLabelColour(this.labelColour));
    pieSeries.labels.template.fontSize = 10;
    pieSeries.labels.template.maxWidth = 100;
    pieSeries.labels.template.wrap = true;
    pieSeries.labels.template.draggable = true;
    pieSeries.labels.template.radius = 15;
    pieSeries.labels.template.text = '{category} {value.percent}%';
    pieSeries.slices.template.tooltipText = 'Estimated Value: {estimatedValue} \n  CostPrice: {costPrice}  \n Profit/Loss: {grossMargin}';
    this.chartObject.responsive.enabled = true;
     unuseTheme(theme);
     this.chartObject.data = data;
     this.excelData = {
       fileName: 'Portfolio by Type',
       data: { Items: data }
     };
     await readyPromise;
  }

  override configChanged(key: keyof PortfolioByTypeWidget) {
    switch (key) {
      case 'radius':
        this.chartObject.radius = percent(this.radius ?? 60);
        break;
      case 'otherThreshold':
        this.grouper.threshold = this.otherThreshold;
        this.refresh$.next();
        break;
      case 'labelColour':
        this.chartObject.series.each(x => x.labels.template.fill = x.ticks.template.stroke = color(this.getChartLabelColour(this.labelColour)));
        break;
      default:
        this.refresh$.next();
        break;
    }
  }
}
